import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  WEEKDAYS,
  WEEKDAY_LABELS,
  type RestrictionEntry,
  type BookingRestrictionParams,
  type RuleFormProps,
  type Weekday,
} from '@/lib/pricingRules';

// booking_restriction: a list of restriction entries, each scoped by an
// OPTIONAL absolute date range ("YYYY-MM-DD", native date inputs) and/or an
// OPTIONAL weekday multiselect, applying cta (closed to arrival) / ctd
// (closed to departure) / stop_sell — three required booleans always
// emitted. Mirrors the local string-draft model from DateSpecificRuleForm
// (the reference form); from/to/days are omitted from the emitted entry
// when empty.

interface EntryDraft {
  from: string;
  to: string;
  days: Weekday[];
  cta: boolean;
  ctd: boolean;
  stopSell: boolean;
}

function entryToDraft(e: RestrictionEntry): EntryDraft {
  return {
    from: e.from ?? '',
    to: e.to ?? '',
    days: (e.days ?? []).filter((d): d is Weekday => (WEEKDAYS as readonly string[]).includes(d)),
    cta: e.cta,
    ctd: e.ctd,
    stopSell: e.stop_sell,
  };
}

function draftToEntry(d: EntryDraft): RestrictionEntry {
  const e: RestrictionEntry = { cta: d.cta, ctd: d.ctd, stop_sell: d.stopSell };
  const from = d.from.trim();
  if (from !== '') e.from = from;
  const to = d.to.trim();
  if (to !== '') e.to = to;
  if (d.days.length > 0) e.days = d.days;
  return e;
}

export function BookingRestrictionRuleForm({ params, onChange }: RuleFormProps<BookingRestrictionParams>) {
  const [drafts, setDrafts] = useState<EntryDraft[]>(() =>
    (params.entries ?? []).map(entryToDraft),
  );

  function commit(next: EntryDraft[]) {
    setDrafts(next);
    onChange({ entries: next.map(draftToEntry) });
  }

  function patch(i: number, p: Partial<EntryDraft>) {
    commit(drafts.map((d, idx) => (idx === i ? { ...d, ...p } : d)));
  }

  function toggleDay(i: number, day: Weekday) {
    const d = drafts[i];
    if (!d) return;
    const days = d.days.includes(day) ? d.days.filter((x) => x !== day) : [...d.days, day];
    patch(i, { days });
  }

  return (
    <div className="flex flex-col gap-3">
      {drafts.map((d, i) => (
        <div
          key={i}
          className="rounded-[var(--radius-control)] border border-[var(--border-hairline)] bg-[var(--surface-sunken)] p-3"
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input
              label="From"
              type="date"
              value={d.from}
              onChange={(e) => patch(i, { from: e.target.value })}
            />
            <Input
              label="To"
              type="date"
              value={d.to}
              onChange={(e) => patch(i, { to: e.target.value })}
            />
          </div>

          <div className="mt-3">
            <div className="text-sm font-semibold text-[var(--text-heading)]">Weekdays</div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2">
              {WEEKDAYS.map((day) => (
                <Checkbox
                  key={day}
                  label={WEEKDAY_LABELS[day]}
                  checked={d.days.includes(day)}
                  onChange={() => toggleDay(i, day)}
                />
              ))}
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
            <Checkbox
              label="Closed to arrival"
              checked={d.cta}
              onChange={(e) => patch(i, { cta: e.target.checked })}
            />
            <Checkbox
              label="Closed to departure"
              checked={d.ctd}
              onChange={(e) => patch(i, { ctd: e.target.checked })}
            />
            <Checkbox
              label="Stop sell"
              checked={d.stopSell}
              onChange={(e) => patch(i, { stopSell: e.target.checked })}
            />
          </div>

          <div className="mt-3 flex justify-end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => commit(drafts.filter((_, idx) => idx !== i))}
            >
              <Trash2 className="h-4 w-4" />
              Remove restriction
            </Button>
          </div>
        </div>
      ))}

      <div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() =>
            commit([...drafts, { from: '', to: '', days: [], cta: false, ctd: false, stopSell: false }])
          }
        >
          <Plus className="h-4 w-4" />
          Add restriction
        </Button>
      </div>
    </div>
  );
}
